import { useState, useEffect, useContext, useRef } from 'react';
// Import Shared Utils
import { get, post } from 'shared-components/src/utils/http';
// Import Local Utils
import { ProgramConfigContext } from '../contexts/ProgramConfigContext';
import { reverifyPassword } from '../routes/Profile/components/PasswordConfirmModal';

// Self-serve card replacement (BP-246) as seen from the dashboard:
//   NONE        - nothing to show (feature off, or a virtual-only program)
//   AVAILABLE   - Replace card is offered
//   IN_PROGRESS - a replacement was ordered and the new card is not activated yet
export const CARD_REPLACEMENT_STATES = {
  NONE: 'none',
  AVAILABLE: 'available',
  IN_PROGRESS: 'in_progress',
};

// Where the open dialog is. FORM covers the three steps (reason, delivery, confirm),
// which the dialog walks itself; everything else replaces the steps.
export const REPLACEMENT_PHASES = {
  LOADING: 'loading',
  QUOTE_FAILED: 'quote_failed',
  INELIGIBLE: 'ineligible',
  UNAVAILABLE: 'unavailable',
  ALREADY_ORDERED: 'already_ordered',
  FORM: 'form',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  FAILED: 'failed',
};

// Password problems keep the cardholder on the confirm step.
export const PASSWORD_ERRORS = {
  WRONG: 'wrong_password',
  STEP_UP: 'step_up',
};

// Standard mail, the only method; fixed here rather than read from the server.
export const DEFAULT_DELIVERY_WINDOW = { min_business_days: 7, max_business_days: 21 };

const replacementUrl = accountId =>
  `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/reissue`;

const balanceUrl = accountId =>
  `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/balance`;

// The available balance in cents, read fresh for the dialog; null when it cannot be read,
// and the dialog then leaves the balance rows out rather than failing.
const readBalanceCents = async accountId => {
  try {
    const res = await get(balanceUrl(accountId));
    const cents = Math.round(parseFloat(res.data.balance) * 100);
    return Number.isFinite(cents) ? cents : null;
  } catch (e) {
    return null;
  }
};

const requestedAtFromAccount = acc => (acc && acc.reissue_requested_at) || null;
// The card the replacement cancelled, for the banner; null when it cannot be told apart.
const previousLastFourFromAccount = acc =>
  (acc && acc.reissue_previous_last_four) || null;

// Replacement is for physical cards only; a virtual program never offers it.
const isVirtualProgram = programConfigs =>
  Boolean(
    programConfigs &&
      programConfigs.program_type &&
      programConfigs.program_type.includes('virtual')
  );

// onReplaced runs once the cardholder leaves the success screen, so the page can re-read
// the cardholder (the old card is now blocked). Deferring it keeps the page's refresh
// overlay from covering the success screen.
export default (account, { onReplaced } = {}) => {
  const programConfigs = useContext(ProgramConfigContext);
  const enabled = Boolean(
    programConfigs &&
      programConfigs.cardholder_reissue_enabled &&
      !isVirtualProgram(programConfigs)
  );
  const [requestedAt, setRequestedAt] = useState(requestedAtFromAccount(account));
  const [previousLastFour, setPreviousLastFour] = useState(previousLastFourFromAccount(account));
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState(REPLACEMENT_PHASES.LOADING);
  const [quote, setQuote] = useState(null);
  const [balanceCents, setBalanceCents] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  // Which quote request is current: a reply to an older one, or one that lands after the
  // dialog has closed, is ignored so it cannot overwrite what is showing.
  const quoteRequest = useRef(0);

  useEffect(() => {
    setRequestedAt(requestedAtFromAccount(account));
    setPreviousLastFour(previousLastFourFromAccount(account));
  }, [account]);

  // Refusals that mean the page is out of date (already ordered, feature switched off):
  // re-read the account so the dashboard catches up, and say so in the dialog.
  const refresh = async () => {
    try {
      const cardholder = await get('/api/v1/cardholder_portal/cardholder');
      const fresh = cardholder.data.accounts.find(acc => acc.id === account.id);
      setRequestedAt(requestedAtFromAccount(fresh));
      setPreviousLastFour(previousLastFourFromAccount(fresh));
    } catch (e) {
      // The dialog already says what happened; the dashboard catches up on the next load.
    }
  };

  const loadQuote = async () => {
    quoteRequest.current += 1;
    const request = quoteRequest.current;
    const current = () => request === quoteRequest.current;
    setPhase(REPLACEMENT_PHASES.LOADING);
    try {
      const res = await get(replacementUrl(account.id));
      if (!current()) return;
      // The quote does not carry the balance. Read it only for an eligible card, the one
      // case the delivery step shows it, so it is as of now rather than the dashboard's load.
      if (res.data.eligible) {
        const cents = await readBalanceCents(account.id);
        if (!current()) return;
        setBalanceCents(cents);
      }
      setQuote(res.data);
      setPhase(
        res.data.eligible ? REPLACEMENT_PHASES.FORM : REPLACEMENT_PHASES.INELIGIBLE
      );
    } catch (e) {
      if (!current()) return;
      if (e.status === 409) {
        setPhase(REPLACEMENT_PHASES.ALREADY_ORDERED);
        refresh();
      } else if (e.status === 403) {
        setPhase(REPLACEMENT_PHASES.UNAVAILABLE);
      } else {
        setPhase(REPLACEMENT_PHASES.QUOTE_FAILED);
      }
    }
  };

  const open = () => {
    setQuote(null);
    setBalanceCents(null);
    setPasswordError(null);
    setIsOpen(true);
    loadQuote();
  };

  const close = () => {
    const replaced = phase === REPLACEMENT_PHASES.SUCCESS;
    // A quote still on its way belongs to this opening; drop it.
    quoteRequest.current += 1;
    setIsOpen(false);
    if (replaced && onReplaced) {
      // The order itself succeeded; a failed re-read must not undo it.
      Promise.resolve()
        .then(onReplaced)
        .catch(() => {});
    }
  };

  // Back to the confirm step after a failed order; nothing was cancelled or charged.
  const retry = () => {
    setPasswordError(null);
    setPhase(REPLACEMENT_PHASES.FORM);
  };

  const submit = async ({ reason, password }) => {
    setPasswordError(null);
    setPhase(REPLACEMENT_PHASES.SUBMITTING);

    // Step-up first: the order is irreversible, so the password is asked for again.
    try {
      await reverifyPassword(password);
    } catch (e) {
      if (e.status === 401) {
        setPasswordError(PASSWORD_ERRORS.WRONG);
        setPhase(REPLACEMENT_PHASES.FORM);
      } else {
        setPhase(REPLACEMENT_PHASES.FAILED);
      }
      return;
    }

    try {
      const res = await post(replacementUrl(account.id), {
        last_four_digits: quote.last_four_digits,
        reason,
        surface: 'portal',
        disclosures_accepted: true,
      });
      setRequestedAt(res.data.requested_at);
      setPhase(REPLACEMENT_PHASES.SUCCESS);
    } catch (e) {
      // Never raw error text: each refusal maps to its own copy.
      switch (e.code) {
      case 'step_up_required':
        setPasswordError(PASSWORD_ERRORS.STEP_UP);
        setPhase(REPLACEMENT_PHASES.FORM);
        break;
      case 'reissue_in_progress':
        setPhase(REPLACEMENT_PHASES.ALREADY_ORDERED);
        await refresh();
        break;
      case 'reissue_not_enabled':
      case 'reissue_not_supported':
        setPhase(REPLACEMENT_PHASES.UNAVAILABLE);
        await refresh();
        break;
      case 'reissue_insufficient_balance':
      case 'reissue_negative_balance':
        setPhase(REPLACEMENT_PHASES.INELIGIBLE);
        break;
      default:
        // 502 reissue_failed and anything unexpected: the card is untouched.
        setPhase(
          e.status === 409
            ? REPLACEMENT_PHASES.ALREADY_ORDERED
            : REPLACEMENT_PHASES.FAILED
        );
      }
    }
  };

  // Typing a new password dismisses the last one's error.
  const clearPasswordError = () => setPasswordError(null);

  let state = CARD_REPLACEMENT_STATES.NONE;
  if (enabled) {
    state = requestedAt
      ? CARD_REPLACEMENT_STATES.IN_PROGRESS
      : CARD_REPLACEMENT_STATES.AVAILABLE;
  }

  return {
    state,
    requestedAt,
    previousLastFour,
    isOpen,
    phase,
    quote,
    balanceCents,
    passwordError,
    clearPasswordError,
    open,
    close,
    retry,
    submit,
  };
};
